// Create new scrape job
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import scrapeJobApi from '../services/scrapeJobApi';
import { browserSessionsApi } from '../services/api';

const NewScrapeJob = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    targetUrl: '',
    maxLinksToCrawl: 2000,
    parallelSessions: 3,
    browserbaseProjectId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Browser sessions state
  const [browserSessions, setBrowserSessions] = useState([]);
  const [browserSessionsError, setBrowserSessionsError] = useState(null);

  // Fetch browser sessions on component mount
  useEffect(() => {
    const fetchBrowserSessions = async () => {
      try {
        setBrowserSessionsError(null);
        console.log('NewScrapeJob: Fetching browser sessions...');
        
        const sessions = await browserSessionsApi.getBrowserSessions();
        console.log('NewScrapeJob: Browser sessions response:', sessions);
        
        if (sessions && Array.isArray(sessions)) {
          setBrowserSessions(sessions);
        } else {
          console.log('NewScrapeJob: No browser sessions returned or invalid format');
          setBrowserSessions([]);
        }
      } catch (err) {
        console.error('Error fetching browser sessions:', err);
        setBrowserSessionsError(err.message);
        setBrowserSessions([]);
      }
    };

    fetchBrowserSessions();
  }, []);

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    // Convert number inputs to numbers
    const parsedValue = type === 'number' ? (value ? parseInt(value, 10) : '') : value;
    
    setFormData({
      ...formData,
      [name]: parsedValue
    });
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.targetUrl) {
      setError('Target URL is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Create the scrape job
      const newJob = await scrapeJobApi.createScrapeJob(formData);
      
      // Redirect to the job details page
      navigate(`/advanced/scrape-jobs/${newJob.jobId}`);
    } catch (err) {
      setError(err.message);
      console.error('Error creating scrape job:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Scrape Job</h1>
        <p className="mt-2 text-sm text-gray-700">
          Configure a new website crawling job to extract content.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error: {error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Browser Sessions Warning */}
      {browserSessions.length > 0 && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.515 2.625H3.72c-1.345 0-2.188-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>{browserSessions.length} browser session{browserSessions.length === 1 ? '' : 's'}</strong> currently in use (out of max 3). 
                Your new scraping job may need to wait for existing sessions to complete.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Target URL */}
            <div>
              <label htmlFor="targetUrl" className="block text-sm font-medium text-gray-700">
                Target URL <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  type="url"
                  name="targetUrl"
                  id="targetUrl"
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="https://example.com"
                  value={formData.targetUrl}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                The website URL you want to crawl. All links will be discovered starting from this URL.
              </p>
            </div>

            {/* Max Links */}
            <div>
              <label htmlFor="maxLinksToCrawl" className="block text-sm font-medium text-gray-700">
                Maximum Links to Crawl
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="maxLinksToCrawl"
                  id="maxLinksToCrawl"
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  // min="500"
                  value={formData.maxLinksToCrawl}
                  onChange={handleInputChange}
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Maximum number of links to discover and crawl. 
              </p>
            </div>

            {/* Parallel Sessions */}
            <div>
              <label htmlFor="parallelSessions" className="block text-sm font-medium text-gray-700">
                Parallel Sessions
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="parallelSessions"
                  id="parallelSessions"
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  min="1"
                  max="3"
                  value={formData.parallelSessions}
                  onChange={handleInputChange}
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Number of browser sessions to run in parallel (1-3). Default: 3
              </p>
            </div>


            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Link
                to="/advanced/scrape-jobs"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Scrape Job'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewScrapeJob;